/**
 * parseVariants() — KHR_materials_variants parser.
 *
 * Both eyewear and jewelry scenes have identical _parseVariants() methods.
 * This utility extracts the shared logic into a standalone function.
 *
 * The KHR_materials_variants extension stores a list of named variants at
 * the GLTF root (gltf.userData.variants) and per-mesh material mappings
 * (mesh.userData.variantMaterials). This function builds a lookup table
 * for O(1) variant switching.
 *
 * Reference:
 *   https://github.com/KhronosGroup/glTF/tree/main/extensions/2.0/Khronos/KHR_materials_variants
 *
 * Usage:
 *   const THREE = await import('three');
 *   const variants = parseVariants(THREE, gltf);
 *   // variants[0].meshMaterialMap.get(mesh.uuid) → Material
 */

import type { Mesh, Object3D, Material } from 'three';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';
import type { ThreeModule, VariantDef } from './types.js';

/**
 * Parse the KHR_materials_variants extension from a loaded GLTF.
 *
 * The GLTFLoader stores extension data in userData on each mesh/primitive.
 * Top-level variant names live in gltf.userData.variants (set by the
 * KHR_materials_variants plugin). Per-mesh overrides are in
 * mesh.userData.variantMaterials as an array of {variantIndex, material}.
 *
 * @param THREE - Dynamically imported Three.js module (needs Mesh constructor).
 * @param gltf  - Loaded GLTF data from GLTFLoader.
 * @returns Array of VariantDef objects with name + meshMaterialMap.
 */
export function parseVariants(THREE: ThreeModule, gltf: GLTF): VariantDef[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userData = gltf.userData as Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const variantNames: string[] = (userData['variants'] as any[])?.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (v: any) => String(v.name ?? ''),
  ) ?? [];

  if (variantNames.length === 0) return [];

  const defs: VariantDef[] = variantNames.map((name) => ({
    name,
    meshMaterialMap: new Map<string, Material>(),
  }));

  gltf.scene.traverse((obj: Object3D) => {
    if (!(obj instanceof (THREE.Mesh as unknown as typeof Mesh))) return;
    const mesh = obj as Mesh;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const meshUserData = mesh.userData as Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const variantMappings: Array<{ variantIndex: number; material: Material }> =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (meshUserData['variantMaterials'] as any[]) ?? [];

    for (const mapping of variantMappings) {
      const def = defs[mapping.variantIndex];
      if (def) {
        def.meshMaterialMap.set(mesh.uuid, mapping.material);
      }
    }
  });

  return defs;
}
